import { useState, useMemo, useCallback, useEffect } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { supabase } from '@/lib/supabase';

interface Avaliacao {
  id: string;
  formacao_id: string;
  funcionario_id: string;
  nota_geral: number;
  created_at: string;
}

interface SemestreStats {
  ano: number;
  semestre: number;
  label: string;
  formacoes: number;
  totalFormandos: number;
  comAvaliacao: number;
  taxaResposta: number;
  notaMedia: number | null;
  formacoesList: { id: string; nome: string; entidade: string; dataInicio: string; dataFim: string }[];
}

export default function IndicadoresFormacaoPage() {
  const { formacoes, participantes, loading: loadingFormacao } = useFormacao();

  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([]);
  const [loadingAv, setLoadingAv] = useState(true);
  const [anoFiltro, setAnoFiltro] = useState<string>('todos');

  const loadAvaliacoes = useCallback(async () => {
    setLoadingAv(true);
    const { data } = await supabase
      .from('formacao_avaliacoes')
      .select('id, formacao_id, funcionario_id, nota_geral, created_at')
      .order('created_at', { ascending: false });
    setAvaliacoes((data as Avaliacao[]) ?? []);
    setLoadingAv(false);
  }, []);

  useEffect(() => { loadAvaliacoes(); }, [loadAvaliacoes]);

  const anosDisponiveis = useMemo(() => {
    const anos = new Set<number>();
    formacoes.forEach((f) => {
      if (f.data_inicio) {
        const ano = Number(f.data_inicio.slice(0, 4));
        if (ano > 2000 && ano < 2100) anos.add(ano);
      }
    });
    return Array.from(anos).sort((a, b) => b - a);
  }, [formacoes]);

  const statsPorSemestre = useMemo((): SemestreStats[] => {
    const mapa: Record<string, SemestreStats> = {};

    // Para cada formação, determinar ano e semestre
    formacoes.forEach((f) => {
      if (!f.data_inicio) return;
      const ano = Number(f.data_inicio.slice(0, 4));
      const mes = Number(f.data_inicio.slice(5, 7));
      if (ano <= 2000 || ano >= 2100) return;

      const semestre = mes <= 6 ? 1 : 2;
      const chave = `${ano}-S${semestre}`;

      if (!mapa[chave]) {
        mapa[chave] = {
          ano,
          semestre,
          label: `${ano} — S${semestre}`,
          formacoes: 0,
          totalFormandos: 0,
          comAvaliacao: 0,
          taxaResposta: 0,
          notaMedia: null,
          formacoesList: [],
        };
      }

      mapa[chave].formacoes += 1;
      mapa[chave].formacoesList.push({
        id: f.id,
        nome: f.nome,
        entidade: f.entidade_formadora ?? '—',
        dataInicio: f.data_inicio,
        dataFim: f.data_fim ?? f.data_inicio,
      });
    });

    // Contar formandos por semestre (participantes das formações desse semestre)
    Object.values(mapa).forEach((s) => {
      const idsFormacoes = new Set(s.formacoesList.map((f) => f.id));
      const partsDoSemestre = participantes.filter((p) => idsFormacoes.has(p.formacao_id));
      s.totalFormandos = partsDoSemestre.length;

      // Contar quantos desses têm avaliação
      const avaliados = new Set<string>();
      avaliacoes.forEach((a) => {
        if (idsFormacoes.has(a.formacao_id)) {
          avaliados.add(a.funcionario_id);
        }
      });
      s.comAvaliacao = avaliados.size;

      s.taxaResposta = s.totalFormandos > 0 ? Math.round((s.comAvaliacao / s.totalFormandos) * 100) : 0;

      // Nota média das avaliações desse semestre
      const notasSemestre = avaliacoes
        .filter((a) => idsFormacoes.has(a.formacao_id))
        .map((a) => a.nota_geral);
      s.notaMedia = notasSemestre.length > 0
        ? Number((notasSemestre.reduce((s, n) => s + n, 0) / notasSemestre.length).toFixed(1))
        : null;
    });

    let resultado = Object.values(mapa).sort((a, b) => {
      if (a.ano !== b.ano) return b.ano - a.ano;
      return b.semestre - a.semestre;
    });

    if (anoFiltro !== 'todos') {
      resultado = resultado.filter((s) => String(s.ano) === anoFiltro);
    }

    return resultado;
  }, [formacoes, participantes, avaliacoes, anoFiltro]);

  const resumoGlobal = useMemo(() => {
    const totalFormandos = statsPorSemestre.reduce((s, r) => s + r.totalFormandos, 0);
    const totalComAval = statsPorSemestre.reduce((s, r) => s + r.comAvaliacao, 0);
    const taxaGlobal = totalFormandos > 0 ? Math.round((totalComAval / totalFormandos) * 100) : 0;

    const todasNotas = avaliacoes.map((a) => a.nota_geral);
    const notaGlobal = todasNotas.length > 0
      ? Number((todasNotas.reduce((s, n) => s + n, 0) / todasNotas.length).toFixed(1))
      : null;

    return { totalFormandos, totalComAval, taxaGlobal, notaGlobal };
  }, [statsPorSemestre, avaliacoes]);

  const loading = loadingFormacao || loadingAv;

  function corTaxa(taxa: number): string {
    if (taxa >= 80) return 'text-emerald-600';
    if (taxa >= 50) return 'text-amber-600';
    if (taxa > 0) return 'text-rose-500';
    return 'text-zinc-300';
  }

  function bgTaxa(taxa: number): string {
    if (taxa >= 80) return 'bg-emerald-500';
    if (taxa >= 50) return 'bg-amber-400';
    if (taxa > 0) return 'bg-rose-400';
    return 'bg-zinc-200';
  }

  function corNota(nota: number | null): string {
    if (nota === null) return 'text-zinc-300';
    if (nota >= 4) return 'text-emerald-600';
    if (nota >= 3) return 'text-amber-600';
    return 'text-rose-500';
  }

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-bar-chart-grouped-line text-white text-lg" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Indicadores por Ano e Semestre</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">
                Taxa de resposta ao relatório de avaliação · {resumoGlobal.totalFormandos} formandos no total
              </p>
            </div>
          </div>

          {/* Filtro de ano */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAnoFiltro('todos')}
              className={`whitespace-nowrap px-4 py-2 text-[13px] font-semibold rounded-xl border-2 transition-all cursor-pointer ${
                anoFiltro === 'todos' ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-100 text-zinc-500 bg-white hover:border-zinc-300'
              }`}
            >
              Todos
            </button>
            {anosDisponiveis.map((a) => (
              <button
                key={a}
                onClick={() => setAnoFiltro(String(a))}
                className={`whitespace-nowrap px-4 py-2 text-[13px] font-semibold rounded-xl border-2 transition-all cursor-pointer ${
                  anoFiltro === String(a) ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-100 text-zinc-500 bg-white hover:border-zinc-300'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">
        {/* Resumo global */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: 'ri-group-line', value: String(resumoGlobal.totalFormandos), label: 'Formandos no Período', sub: `${statsPorSemestre.length} semestre(s)`, bg: 'bg-zinc-100 text-zinc-600' },
            { icon: 'ri-survey-line', value: String(resumoGlobal.totalComAval), label: 'Relatórios Preenchidos', sub: `${resumoGlobal.taxaGlobal}% taxa de resposta`, bg: resumoGlobal.taxaGlobal >= 80 ? 'bg-emerald-50 text-emerald-600' : resumoGlobal.taxaGlobal >= 50 ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600' },
            { icon: 'ri-star-fill', value: resumoGlobal.notaGlobal ? resumoGlobal.notaGlobal.toFixed(1) : '—', label: 'Nota Média Global', sub: resumoGlobal.notaGlobal ? 'das avaliações recebidas' : 'sem avaliações', bg: resumoGlobal.notaGlobal && resumoGlobal.notaGlobal >= 4 ? 'bg-emerald-50 text-emerald-600' : resumoGlobal.notaGlobal && resumoGlobal.notaGlobal >= 3 ? 'bg-amber-50 text-amber-600' : 'bg-zinc-100 text-zinc-600' },
            { icon: 'ri-book-open-line', value: String(statsPorSemestre.reduce((s, r) => s + r.formacoes, 0)), label: 'Ações de Formação', sub: 'no período selecionado', bg: 'bg-zinc-100 text-zinc-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4">
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.bg}`}>
                <i className={`${s.icon} text-lg`} />
              </div>
              <div>
                <p className="text-[22px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                <p className="text-[11px] font-semibold text-zinc-500 mt-0.5">{s.label}</p>
                <p className="text-[10px] text-zinc-400">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
            <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
            <p className="text-[13px] text-zinc-400">A carregar indicadores...</p>
          </div>
        ) : statsPorSemestre.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
            <i className="ri-bar-chart-grouped-line text-4xl text-zinc-200" />
            <p className="text-[14px] font-semibold text-zinc-400">Sem dados para o período selecionado</p>
            <p className="text-[12px] text-zinc-400">Não existem formações registadas com datas no intervalo escolhido.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {statsPorSemestre.map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                {/* Cabeçalho do semestre */}
                <div className="px-6 py-5 border-b border-zinc-100">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                        <span className="text-[14px] font-black text-white">S{s.semestre}</span>
                      </div>
                      <div>
                        <h2 className="text-[16px] font-bold text-zinc-900">{s.ano}</h2>
                        <p className="text-[12px] text-zinc-400">
                          {s.semestre === 1 ? 'Janeiro a Junho' : 'Julho a Dezembro'} · {s.formacoes} ação(ões)
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-8 flex-shrink-0">
                      {/* Formandos */}
                      <div className="text-center">
                        <p className="text-[22px] font-black text-zinc-900">{s.totalFormandos}</p>
                        <p className="text-[10px] text-zinc-400 font-medium">Formandos</p>
                      </div>

                      {/* Com avaliação */}
                      <div className="text-center">
                        <p className={`text-[22px] font-black ${corTaxa(s.taxaResposta)}`}>
                          {s.comAvaliacao}
                        </p>
                        <p className="text-[10px] text-zinc-400 font-medium">Relatórios</p>
                      </div>

                      {/* Taxa de resposta */}
                      <div className="text-center">
                        <p className={`text-[22px] font-black ${corTaxa(s.taxaResposta)}`}>
                          {s.taxaResposta}%
                        </p>
                        <p className="text-[10px] text-zinc-400 font-medium">Taxa Resposta</p>
                      </div>

                      {/* Nota média */}
                      <div className="text-center">
                        <p className={`text-[22px] font-black ${corNota(s.notaMedia)}`}>
                          {s.notaMedia ? s.notaMedia.toFixed(1) : '—'}
                        </p>
                        <p className="text-[10px] text-zinc-400 font-medium">Nota Média</p>
                      </div>
                    </div>
                  </div>

                  {/* Barra de progresso da taxa de resposta */}
                  <div className="mt-4 flex items-center gap-3">
                    <span className="text-[11px] text-zinc-400 w-28 flex-shrink-0">Taxa de resposta</span>
                    <div className="flex-1 h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${bgTaxa(s.taxaResposta)}`}
                        style={{ width: `${s.taxaResposta}%` }}
                      />
                    </div>
                    <span className={`text-[12px] font-bold w-12 text-right flex-shrink-0 ${corTaxa(s.taxaResposta)}`}>
                      {s.taxaResposta}%
                    </span>
                  </div>
                </div>

                {/* Lista de formações do semestre */}
                <div className="divide-y divide-zinc-50">
                  {s.formacoesList
                    .sort((a, b) => new Date(b.dataInicio).getTime() - new Date(a.dataInicio).getTime())
                    .map((f) => {
                      const partsFormacao = participantes.filter((p) => p.formacao_id === f.id);
                      const avalFormacao = avaliacoes.filter((a) => a.formacao_id === f.id);
                      const avaliadosUnicos = new Set(avalFormacao.map((a) => a.funcionario_id)).size;
                      const taxaForm = partsFormacao.length > 0 ? Math.round((avaliadosUnicos / partsFormacao.length) * 100) : 0;
                      const notaForm = avalFormacao.length > 0
                        ? Number((avalFormacao.reduce((sum, a) => sum + a.nota_geral, 0) / avalFormacao.length).toFixed(1))
                        : null;

                      return (
                        <div key={f.id} className="px-6 py-3.5 flex items-center gap-4 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] text-zinc-400">{f.entidade}</span>
                              <span className="text-[10px] text-zinc-300">·</span>
                              <span className="text-[11px] text-zinc-400">
                                {new Date(f.dataInicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                                {f.dataFim && f.dataFim !== f.dataInicio ? ` a ${new Date(f.dataFim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-6 flex-shrink-0">
                            <div className="text-center min-w-[50px]">
                              <p className="text-[13px] font-bold text-zinc-700">{partsFormacao.length}</p>
                              <p className="text-[10px] text-zinc-400">formandos</p>
                            </div>
                            <div className="text-center min-w-[50px]">
                              <p className={`text-[13px] font-bold ${corTaxa(taxaForm)}`}>{avaliadosUnicos}</p>
                              <p className="text-[10px] text-zinc-400">avaliações</p>
                            </div>
                            <div className="text-center min-w-[50px]">
                              <p className={`text-[13px] font-bold ${corTaxa(taxaForm)}`}>{taxaForm}%</p>
                              <p className="text-[10px] text-zinc-400">taxa</p>
                            </div>
                            <div className="text-center min-w-[50px]">
                              <p className={`text-[13px] font-bold ${corNota(notaForm)}`}>{notaForm ? notaForm.toFixed(1) : '—'}</p>
                              <p className="text-[10px] text-zinc-400">nota</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}